import { ReactEditor } from '@/components/Editor/HmqcEditor'
import {
  type getClusterRevisionFormSchema,
  safelyParseJsonString,
} from '@/features/ClusterConfigs/components/ClusterRevisionForm/schema'
import { DeploymentLabelCombobox } from '@/features/ClusterConfigs/components/DeploymentLabelCombobox'
import { ProductSelect } from '@/features/ClusterConfigs/components/ProductSelect/ProductSelect'
import { TemplateCombobox } from '@/features/ClusterConfigs/components/TemplateCombobox/TemplateCombobox'
import type { ClusterRevision } from '@/generated/http-clients/v3ApiSchemas'
import {
  Badge,
  Field,
  HStack,
  Heading,
  Input,
  Spinner,
  VStack,
} from '@chakra-ui/react'
import type {
  ReactFormExtendedApi,
  StandardSchemaV1,
} from '@tanstack/react-form'
import { omit } from 'lodash'
import type { PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'
import type { z } from 'zod'

export const JSON_EDITOR_TAB_SIZE = 2

const CONFIG_SECTIONS = ['system', 'customer', 'override'] as const

export type RevisionSection = (typeof CONFIG_SECTIONS)[number]
export type RevisionFormValues = z.infer<
  ReturnType<typeof getClusterRevisionFormSchema>
>

export enum RevisionFormMode {
  REVISION_DRAFT = 'REVISION_DRAFT',
  NEW_CLUSTER = 'NEW_CLUSTER',
}

// Tanstack form does not provide a nice way for typing a form
export type ClusterRevisionFormType = ReactFormExtendedApi<
  RevisionFormValues,
  undefined,
  StandardSchemaV1<RevisionFormValues, unknown>,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  StandardSchemaV1<RevisionFormValues, unknown>,
  undefined,
  undefined,
  undefined
>

type ClusterRevisionFormProps = PropsWithChildren<
  Readonly<{
    mode: RevisionFormMode
    form: ClusterRevisionFormType
  }>
>

export function ClusterRevisionForm({
  mode,
  form,
  children,
}: ClusterRevisionFormProps) {
  const { t } = useTranslation()
  return (
    <VStack width="100%" alignItems="stretch" gap={6}>
      <HStack
        alignItems="start"
        display="grid"
        gridRowGap={6}
        gridColumnGap={4}
        gridTemplateColumns="1fr"
        xl={{ gridTemplateColumns: '1fr 1fr' }}
      >
        {children}
        <form.Field name="name">
          {(field) => {
            const errorMessage = field.state.meta.errors[0]?.message
            const isInvalid = field.state.meta.isTouched && !!errorMessage

            return (
              <Field.Root invalid={isInvalid}>
                <Field.Label htmlFor="name">
                  {t('newClusterForm.fields.name.label')}
                </Field.Label>
                <Input
                  id="name"
                  name="name"
                  placeholder={String(
                    t('newClusterForm.fields.name.placeholder'),
                  )}
                  backgroundColor="bg"
                  borderColor={isInvalid ? 'content.error' : 'border'}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  aria-invalid={isInvalid}
                  disabled={mode === RevisionFormMode.REVISION_DRAFT}
                />
                {isInvalid && errorMessage && (
                  <Field.ErrorText>{errorMessage}</Field.ErrorText>
                )}
                {!isInvalid && mode !== RevisionFormMode.REVISION_DRAFT && (
                  <Field.HelperText>
                    {t('newClusterForm.fields.name.helperText')}
                  </Field.HelperText>
                )}
              </Field.Root>
            )
          }}
        </form.Field>
        <form.Field name="deploymentLabel">
          {(field) => (
            <DeploymentLabelCombobox
              value={field.state.value}
              onChange={(value) => field.handleChange(value ?? '')}
              onBlur={field.handleBlur}
              disabled={mode === RevisionFormMode.REVISION_DRAFT}
              error={
                field.state.meta.isTouched
                  ? field.state.meta.errors[0]?.message
                  : undefined
              }
            />
          )}
        </form.Field>
        <form.Field name="productId">
          {(field) => (
            <ProductSelect
              // @ts-expect-error productId field is required in schema but user can still remove selection, then value can be undefined
              onChange={field.handleChange}
              onBlur={field.handleBlur}
              value={field.state.value}
              error={
                field.state.meta.isTouched
                  ? field.state.meta.errors[0]?.message
                  : undefined
              }
            />
          )}
        </form.Field>
        <form.Field name="templateId">
          {(field) => (
            <TemplateCombobox
              value={field.state.value}
              onChange={(value) => field.handleChange(value ?? '')}
              onBlur={field.handleBlur}
              error={
                field.state.meta.isTouched
                  ? field.state.meta.errors[0]?.message
                  : undefined
              }
            />
          )}
        </form.Field>
      </HStack>

      {CONFIG_SECTIONS.map((section) => (
        <ConfigSectionWithForm
          key={section}
          form={form}
          section={section}
          isReadonly={false}
        />
      ))}
    </VStack>
  )
}

export function ClusterRevisionReadonlyForm({
  data,
}: Readonly<{
  data: ClusterRevision
}>) {
  const { t } = useTranslation()
  return (
    <VStack width="100%" alignItems="stretch" gap={6}>
      <HStack
        alignItems="start"
        display="grid"
        gridRowGap={6}
        gridColumnGap={4}
        gridTemplateColumns="1fr"
        xl={{ gridTemplateColumns: '1fr 1fr' }}
      >
        <Field.Root>
          <Field.Label htmlFor="name">
            {t('newClusterForm.fields.name.label')}
          </Field.Label>
          <Input
            id="name"
            name="name"
            backgroundColor="bg"
            value={data.hive_name}
            disabled
          />
        </Field.Root>
        <DeploymentLabelCombobox value={data.deployment_label} disabled />
        <ProductSelect value={data.product_id} disabled />
        <TemplateCombobox value={data.template_id} disabled />
      </HStack>

      {CONFIG_SECTIONS.map((section) => (
        <ConfigSection
          key={section}
          section={section}
          value={JSON.stringify(
            data[section] ?? {},
            null,
            JSON_EDITOR_TAB_SIZE,
          )}
          isReadonly
        />
      ))}
    </VStack>
  )
}

function ConfigSectionWithForm<S extends RevisionSection>({
  section,
  isReadonly,
  form,
}: Readonly<{
  section: S
  isReadonly: boolean
  form: ClusterRevisionFormType
}>) {
  return (
    <form.Field name={`_${section}_raw_json`}>
      {(field) => (
        <ConfigSection
          value={field.state.value}
          onChange={(value) => {
            //   @ts-expect-error due to dynamic field name the type for value here is incompatible
            field.handleChange(value)
            const parsedValue = safelyParseJsonString(value || '{}')
            if (parsedValue) {
              //   @ts-expect-error due to dynamic field name the type for value here is incompatible
              field.form.setFieldValue(section, parsedValue)
            }
            field.handleBlur()
          }}
          section={section}
          isReadonly={isReadonly}
          error={field.state.meta.errors[0]?.message}
        />
      )}
    </form.Field>
  )
}

function ConfigSection<S extends RevisionSection>({
  section,
  value,
  onChange,
  isReadonly,
  error,
}: Readonly<{
  section: S
  value: string
  isReadonly: boolean
  onChange?: (value: string) => void
  error?: string
}>) {
  const { t } = useTranslation('features', { useSuspense: false })

  const editorOptions = {
    language: 'json',
    automaticLayout: true,
    scrollBeyondLastLine: false,
    tabSize: JSON_EDITOR_TAB_SIZE,
    insertSpaces: true,
    readOnly: isReadonly,
  }

  return (
    <VStack alignItems="stretch" gap={2}>
      <Heading as="h2" size="lg">
        <HStack alignItems="center">
          {getConfigSectionTitle(section, t)}
          {isReadonly && (
            <Badge>{t('clusterRevisionForm.badges.readonlyBadge')}</Badge>
          )}
          {error && <Badge colorPalette="red">{error}</Badge>}
        </HStack>
      </Heading>
      <ReactEditor
        language="json"
        loading={<Spinner my={8} />}
        value={value}
        options={editorOptions}
        onChange={(value) => onChange?.(value ?? '')}
      />
    </VStack>
  )
}

function getConfigSectionTitle(
  section: RevisionSection,
  t: (key: string) => string,
) {
  switch (section) {
    case 'system':
      return t('clusterRevisionForm.sections.system')
    case 'customer':
      return t('clusterRevisionForm.sections.customer')
    case 'override':
      return t('clusterRevisionForm.sections.override')
  }
}

export function getSystemConfigWithoutReadonlyFields(
  systemConfig: ClusterRevision['system'],
) {
  if (!systemConfig) {
    return { global: {} } // default config
  }

  const globalWithoutReadonlyFields = omit(systemConfig.global, [
    'name',
    'deployment_label',
  ])

  return {
    ...systemConfig,
    global: globalWithoutReadonlyFields,
  }
}

export function getSystemConfigWithReadonlyFields(
  systemConfig: NonNullable<ClusterRevision['system']>,
  { name, deployment_label }: { name: string; deployment_label: string },
) {
  return {
    ...systemConfig,
    global: {
      ...systemConfig.global,
      name,
      deployment_label,
    },
  }
}
